import {useCallback, useEffect, useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {getLocalStorageItem, setLocalStorageItem} from '../shared/localStorageUtil';
import {useCountryLang} from './useCountryLang';
import {request} from '../shared/httpInterceptor';

const STORAGE_KEY = 'recentlyViewedProducts';
const MAX_PRODUCTS = 10;

/**
 * Fetches product data for a list of product IDs
 */
export const fetchProductsByIds = async (productIds: number[], countryLang: string | null) => {
  if (!productIds.length || !countryLang) return [];

  try {
    const res = await request({
      url: `/${countryLang}/product/resolve`,
      method: 'post',
      data: {
        productIds,
        format: "NORMAL"
      }
    });
    return res.data || [];
  } catch (error) {
    console.error('Error fetching products by IDs:', error);
    return [];
  }
};

/**
 * Hook to track and retrieve recently viewed products
 */
export const useRecentlyViewedProducts = () => {
  const countryLang = useCountryLang();
  const [recentProductIds, setRecentProductIds] = useState<number[]>([]);

  // Load product IDs from localStorage on initial mount
  useEffect(() => {
    try {
      const storedProducts = getLocalStorageItem(STORAGE_KEY);
      if (storedProducts) {
        const parsedProducts = JSON.parse(storedProducts);
        if (Array.isArray(parsedProducts)) {
          setRecentProductIds(parsedProducts);
        }
      }
    } catch (e) {
      console.error('Error parsing recently viewed products from localStorage', e);
    }
  }, []);

  // Fetch the product data from the API
  const recentProductsQuery = useQuery(
    ['recentlyViewedProducts', recentProductIds, countryLang],
    () => fetchProductsByIds(recentProductIds, countryLang),
    {
      enabled: recentProductIds.length > 0 && countryLang != null,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  /**
   * Add a product ID to the recently viewed list
   * Using useCallback to maintain reference stability
   */
  const addProduct = useCallback((productId: number) => {
    setRecentProductIds(prevIds => {
      // Check if product is already in the list at the front
      if (prevIds.length > 0 && prevIds[0] === productId) {
        return prevIds; // Avoid unnecessary updates
      }

      // Remove the product if it already exists in the list
      const filteredIds = prevIds.filter(id => id !== productId);

      // Add the new product ID to the beginning of the array
      const newIds = [productId, ...filteredIds].slice(0, MAX_PRODUCTS);

      // Save to localStorage asynchronously
      setTimeout(() => {
        try {
          setLocalStorageItem(STORAGE_KEY, JSON.stringify(newIds));
        } catch (e) {
          console.error('Error saving recently viewed products to localStorage', e);
        }
      }, 0);

      return newIds;
    });
  }, []);

  /**
   * Remove a product ID from the recently viewed list
   */
  const removeProduct = useCallback((productId: number) => {
    setRecentProductIds(prevIds => {
      // Filter out the product ID to remove
      const newIds = prevIds.filter(id => id !== productId);

      // Save to localStorage asynchronously
      setTimeout(() => {
        try {
          setLocalStorageItem(STORAGE_KEY, JSON.stringify(newIds));
        } catch (e) {
          console.error('Error saving recently viewed products to localStorage', e);
        }
      }, 0);

      return newIds;
    });
  }, []);

  /**
   * Clear all products from the recently viewed list
   */
  const clearAllProducts = useCallback(() => {
    setRecentProductIds([]);

    // Remove from localStorage asynchronously
    setTimeout(() => {
      try {
        setLocalStorageItem(STORAGE_KEY, JSON.stringify([]));
      } catch (e) {
        console.error('Error clearing recently viewed products from localStorage', e);
      }
    }, 0);
  }, []);

  return {
    recentProducts: recentProductsQuery.data || [],
    addProduct,
    removeProduct,
    clearAllProducts,
    isLoading: recentProductsQuery.isLoading,
    isError: recentProductsQuery.isError,
  };
}; 